import {
  ReloadOptions,
  RowCustomAction,
  RowCustomActionArgs,
  sendNotification,
  useApiClient,
  useAsyncCache,
  useModuleFederationContext,
  useNavigation,
  useTranslation,
  useUrlContext,
} from '@msdk/common'
import {
  CustomActionDefinition,
  Entity,
  EntityMetadata,
  GenericFieldValue,
  OperatorType,
  ViewAcl,
} from '@msdk/definitions'
import { useEffect, useState } from 'react'

import { resolveItem } from '../utils'
import { useComponentModal, useDialogInputModal, useDialogModal, useFormModal, useViewModal } from '.'

export const useRowActions = (
  actions: CustomActionDefinition[],
  row: Entity,
  reload: () => void,
  baseMetadata: EntityMetadata,
  viewAcl: ViewAcl,
  addOrUpdateFilter: (fieldName: string, operator: OperatorType, value: GenericFieldValue) => void,
  refreshView?: (viewName: string) => void,
) => {
  const [actionInstances, setActionInstances] = useState<Record<string, RowCustomAction>>({})
  const apiClient = useApiClient()
  const formModal = useFormModal()
  const viewModal = useViewModal()
  const componentModal = useComponentModal()
  const dialogModal = useDialogModal()
  const dialogInputModal = useDialogInputModal()
  const asyncCache = useAsyncCache()
  const t = useTranslation()
  const { context } = useUrlContext()
  const navigate = useNavigation()
  const modules = useModuleFederationContext()
  const module = modules.loadedModules[baseMetadata.msProvider]

  useEffect(() => {
    const initActions = async () => {
      if (actions) {
        const instances: Record<string, RowCustomAction> = {}
        const initPromises: Promise<void>[] = []
        actions.forEach((a) => {
          if (module) {
            const ctor = resolveItem(module, a.fullName)
            if (!ctor) {
              sendNotification({
                message: t(
                  'CMPSR_SERVICE_CUSTOM_ACTION_VIEW_ERROR',
                  'The view is missing some actions and it may not work properly.',
                ),
                status: 'error',
              })
              console.error(`[ViewActions] - custom action not found, ${a.fullName}`)
              return
            }
            const combinedReload = (options?: ReloadOptions) => {
              if (!options) {
                reload()
              } else {
                options.views.forEach((viewName) => refreshView?.(viewName))
              }
            }
            const args: RowCustomActionArgs = {
              apiClient,
              modals: { formModal, viewModal, componentModal, dialogModal, dialogInputModal },
              translate: t,
              reload: combinedReload,
              asyncCache,
              row,
              urlContext: context,
              navigate,
              viewAcl,
              addOrUpdateFilter,
              config: [],
            }
            instances[a.name] = new ctor(args)
            initPromises.push(instances[a.name].onInit())
          }
        })
        await Promise.all(initPromises)
        setActionInstances(instances)
      }
    }
    initActions().catch(console.error)
  }, [actions, module])

  /**
   * Synchronize the row data for all action instances with the latest row.
   * This ensures that each action instance reads fresh row data on every render, preventing stale row fields reads after a view refresh.
   */
  Object.values(actionInstances).forEach((instance) => {
    if (typeof instance.setRow === 'function') {
      instance.setRow(row)
    }
  })

  return actionInstances
}
